import { FC, PropsWithChildren } from 'react'

import {
  AutomationSimulationProvider,
  NON_QUOTE_SEMANTIC_TYPES
} from 'Automations/simulation'
import { useCurrentUser } from 'common/hooks'

/**
 * Runs the live quote approval workflow against the quote being edited, so the
 * header, the kebab menu and the sidebar can all say what publishing will
 * actually do. They read it through `useQuoteApproval` rather than from here.
 *
 * Without a signed-in user we've got nothing to simulate as, so we don't start.
 * Everything below then falls back to what it did before, which is the deal
 * this whole thing is built on.
 */
export const QuoteApprovalSimulationProvider: FC<
  PropsWithChildren<{ quoteId: string; isEnabled: boolean }>
> = ({ children, isEnabled, quoteId }) => {
  const { id: requestorId } = useCurrentUser()

  return (
    <AutomationSimulationProvider
      triggerType="QUOTE_REQUIRES_APPROVAL"
      // The two values the trigger is started with, and nothing else.
      // `comment` is defaulted to '' for us inside the simulation, and
      // `signatureConfiguration` is left out on purpose: we'd have to invent one,
      // and a rule reading an invented value answers confidently about nothing.
      // Left out, that rule goes unresolved and we fall back instead.
      workflowInput={{ quoteId, requestorId: requestorId ?? '' }}
      // Everything else belongs to something other than this quote, so a few
      // minutes old will do. The quote itself is what's being edited, so it's
      // fetched again every time.
      useCacheValueFor={NON_QUOTE_SEMANTIC_TYPES}
      enabled={isEnabled && Boolean(quoteId) && Boolean(requestorId)}
    >
      {children}
    </AutomationSimulationProvider>
  )
}
